import React, { useEffect, useState } from 'react';
import { useImpersonation, Alias } from '../context/ImpersonationContext';
import { useAppContext } from '../context/AppContext';
import '../styles/CompanySelectionPage.css';

export const CompanySelectionPage: React.FC = () => {
  const {
    aliases,
    activeAlias,
    isLoading,
    error,
    isImpersonating,
    fetchAliases,
    setActiveAlias,
    clearImpersonation
  } = useImpersonation();
  const { setActivePage } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch aliases when component mounts
  useEffect(() => {
    fetchAliases();
  }, [fetchAliases]);

  // Filter aliases based on search query
  const filteredAliases = (aliases || []).filter(alias =>
    alias.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectCompany = (alias: Alias) => {
    setActiveAlias(alias);
    setActivePage('chat');
  };

  const handleExitImpersonation = () => {
    clearImpersonation();
    setActivePage('chat');
  };

  const handleBack = () => {
    setActivePage('chat');
  };

  const isActiveAlias = (alias: Alias): boolean => {
    return activeAlias?.company_id === alias.company_id;
  };

  return (
    <div className="company-selection-page">
      <div className="company-selection-container">
        <div className="company-selection-header">
          <button className="company-selection-back-btn" onClick={handleBack} aria-label="Retour">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="company-selection-title">Changer de contexte entreprise</h1>
        </div>

        <div className="company-selection-search">
          <svg className="company-selection-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher une entreprise..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="company-selection-search-input"
            aria-label="Rechercher une entreprise"
          />
        </div>

        <div className="company-selection-list">
          {isLoading && (
            <div className="company-selection-loading">
              <div className="company-selection-spinner" />
              <span>Chargement des entreprises...</span>
            </div>
          )}

          {error && !isLoading && (
            <div className="company-selection-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
              <button onClick={fetchAliases} className="company-selection-retry-btn">
                Réessayer
              </button>
            </div>
          )}

          {!isLoading && !error && filteredAliases.length === 0 && (
            <div className="company-selection-empty">
              {searchQuery ? (
                <span>Aucune entreprise ne correspond à "{searchQuery}"</span>
              ) : (
                <span>Aucune entreprise disponible</span>
              )}
            </div>
          )}

          {!isLoading && !error && filteredAliases.map((alias) => (
            <button
              key={alias.company_id}
              className={`company-selection-item ${isActiveAlias(alias) ? 'active' : ''}`}
              onClick={() => handleSelectCompany(alias)}
            >
              <div className="company-selection-item-info">
                <span className="company-selection-item-name">{alias.company_name}</span>
              </div>
              {isActiveAlias(alias) ? (
                <span className="company-selection-item-badge">Actif</span>
              ) : (
                <svg className="company-selection-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {isImpersonating && (
          <div className="company-selection-footer">
            <button
              className="company-selection-exit-btn"
              onClick={handleExitImpersonation}
            >
              Retour au mode normal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
